import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckIcon from '@mui/icons-material/Check';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useProducts, Product } from '../../hooks/useProducts';
import CircularProgress from '@mui/material/CircularProgress';
import ProductCard from '../../components/ProductCard';

const CATEGORIES = [
  { id: 'all', label: 'All Products', count: 1102 },
  { id: 'electrosurgical', label: 'Electrosurgical', count: 145 },
  { id: 'obgyn', label: 'OB/GYN', count: 89 },
  { id: 'forceps', label: 'Forceps & Clamps', count: 320 },
  { id: 'lab', label: 'Laboratory', count: 412 },
  { id: 'leadcare', label: 'LeadCare', count: 15 },
];

const BRANDS = [
  { id: 'bovie', label: 'Bovie Medical' },
  { id: 'miltex', label: 'Miltex Integra' },
  { id: 'welchallyn', label: 'Welch Allyn' },
  { id: 'meridian', label: 'Meridian Bioscience' },
];

const AVAILABILITY = [
  { id: 'in_stock', label: 'In Stock' },
  { id: 'out_of_stock', label: 'Out of Stock' },
  { id: 'pre_order', label: 'Available on Backorder' }
];
const ITEMS_PER_PAGE = 9;

const FilterSection: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-6 border-b border-gray-100 pb-6 last:border-0">
      <button 
        className="w-full flex justify-between items-center py-2 text-sm font-bold text-gray-900 focus:outline-none"
        onClick={() => setOpen(!open)}
      >
        {title}
        {open ? <KeyboardArrowUpIcon fontSize="small" className="text-gray-400" /> : <KeyboardArrowDownIcon fontSize="small" className="text-gray-400" />}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};

// Helper hook to get URL search params
function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const VALID_CATEGORY_IDS = ['all', 'electrosurgical', 'obgyn', 'forceps', 'lab', 'leadcare'];

const ShopProducts: React.FC = () => {
  const query = useQuery();
  const searchQuery = query.get('q') || '';
  const categoryFromUrl = query.get('category') || '';

  const [activeCategory, setActiveCategory] = useState(() => {
    const c = categoryFromUrl.toLowerCase().trim();
    return c && VALID_CATEGORY_IDS.includes(c) ? c : 'all';
  });

  // Sync category filter when URL param changes (e.g. from home category links)
  useEffect(() => {
    const c = categoryFromUrl.toLowerCase().trim();
    setActiveCategory(c && VALID_CATEGORY_IDS.includes(c) ? c : 'all');
  }, [categoryFromUrl]);
  const [activeBrands, setActiveBrands] = useState<string[]>([]);
  const [activeAvailability, setActiveAvailability] = useState<string[]>([]);
  
  // Layout and Pagination state
  const [viewLayout, setViewLayout] = useState<'grid-3' | 'grid-2' | 'list'>('grid-3');
  const [currentPage, setCurrentPage] = useState(1);
  
  const { products, loading, error } = useProducts();

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, activeBrands, activeAvailability, searchQuery]);

  // Apply filters
  const filteredProducts = products.filter((p: Product) => {
    // 1. Search Query Filter
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) && !(p.tag || '').toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
    }
    // 2. Category Filter
    if (activeCategory !== 'all' && p.cat !== activeCategory) {
        return false;
    }
    return true;
  });

  // Calculate Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const toggleArrayItem = (setter: React.Dispatch<React.SetStateAction<string[]>>, array: string[], item: string) => {
    setter(array.includes(item) ? array.filter(i => i !== item) : [...array, item]);
  };

  const clearAllFilters = () => {
    setActiveCategory('all');
    setActiveBrands([]);
    setActiveAvailability([]);
    // To clear search, ideally we'd update useSearchParams if using react-router v6 heavily,
    // but navigating to `/shop` also works.
    if (searchQuery) {
        window.history.pushState({}, '', '/shop');
    }
  };

  // Compact pagination: show at most 7 page slots (e.g. 1 ... 4 5 6 ... 20) to avoid overflow/shake
  const getPaginationPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | 'ellipsis')[] = [];
    const show = 5; // current + 2 left + 2 right
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, currentPage + 2);
    if (end - start + 1 < show) {
      if (start === 1) end = Math.min(totalPages, start + show - 1);
      else start = Math.max(1, end - show + 1);
    }
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('ellipsis');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <section className="py-16 bg-white min-h-screen">
      <div className="container-main">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          
          {/* Sidebar Filters - no overflow on section/container so sticky works on PC */}
          <aside className="w-full lg:w-64 flex-shrink-0 lg:sticky lg:top-20 h-max z-20">
            <h2 className="text-lg font-bold text-gray-900 mb-8 flex items-center justify-between">
              Filter <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block ml-1"></span>
            </h2>

            <FilterSection title="Categories">
              <ul className="space-y-3 max-h-[60vh] overflow-y-auto lg:max-h-none lg:overflow-visible pr-2 custom-scrollbar">
                {CATEGORIES.map(cat => (
                  <li key={cat.id}>
                    <label className="flex items-center cursor-pointer group">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 transition-colors ${
                        activeCategory === cat.id ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'
                      }`}>
                         {activeCategory === cat.id && <CheckIcon sx={{ fontSize: 12 }} className="text-white" />}
                      </div>
                      <span className={`text-sm ${activeCategory === cat.id ? 'font-semibold text-primary' : 'text-gray-600'}`}>
                        {cat.label}
                      </span>
                      <span className="ml-auto text-xs text-gray-400">({cat.count})</span>
                      {/* Hidden radio to make it accessible */}
                      <input 
                        type="radio" 
                        name="category" 
                        value={cat.id} 
                        checked={activeCategory === cat.id} 
                        onChange={() => setActiveCategory(cat.id)} 
                        className="hidden" 
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </FilterSection>

            <FilterSection title="Availability" defaultOpen={false}>
              <ul className="space-y-3">
                {AVAILABILITY.map(status => (
                  <li key={status.id}>
                    <label className="flex items-center cursor-pointer group">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 transition-colors ${
                        activeAvailability.includes(status.id) ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'
                      }`}>
                         {activeAvailability.includes(status.id) && <CheckIcon sx={{ fontSize: 12 }} className="text-white" />}
                      </div>
                      <span className={`text-sm ${activeAvailability.includes(status.id) ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {status.label}
                      </span>
                      <input 
                        type="checkbox" 
                        checked={activeAvailability.includes(status.id)} 
                        onChange={() => toggleArrayItem(setActiveAvailability, activeAvailability, status.id)} 
                        className="hidden" 
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </FilterSection>

            <FilterSection title="Brand" defaultOpen={false}>
              <ul className="space-y-3">
                {BRANDS.map(brand => (
                  <li key={brand.id}>
                    <label className="flex items-center cursor-pointer group">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 transition-colors ${
                        activeBrands.includes(brand.id) ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'
                      }`}>
                         {activeBrands.includes(brand.id) && <CheckIcon sx={{ fontSize: 12 }} className="text-white" />}
                      </div>
                      <span className={`text-sm ${activeBrands.includes(brand.id) ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {brand.label}
                      </span>
                      <input 
                        type="checkbox" 
                        checked={activeBrands.includes(brand.id)} 
                        onChange={() => toggleArrayItem(setActiveBrands, activeBrands, brand.id)} 
                        className="hidden" 
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </FilterSection>

            {/* Clear filters button at bottom on PC only */}
            <div className="hidden lg:block mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={clearAllFilters}
                className="w-full text-sm font-bold text-gray-600 hover:text-primary border border-gray-200 hover:border-primary rounded-xl py-2.5 px-4 transition-colors"
              >
                Clear filters
              </button>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            
            {/* Top Toolbar */}
            <div className="sticky top-20 z-30 bg-white/95 backdrop-blur pt-4 flex flex-col md:flex-row md:items-center justify-between mb-8 pb-4 border-b border-gray-100 gap-4">
              <p className="text-sm text-gray-500 font-medium whitespace-nowrap">
                Showing {Math.min(startIndex + 1, filteredProducts.length)} - {Math.min(startIndex + ITEMS_PER_PAGE, filteredProducts.length)} of {filteredProducts.length} products
                {searchQuery && <span className="ml-2 bg-primary/10 text-primary px-2 py-1 rounded text-xs">Search: "{searchQuery}"</span>}
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 overflow-x-hidden pb-2 md:pb-0">
                
                {/* Active Filter Pills */}
                <div className="flex flex-wrap items-center gap-2">
                  {(activeCategory !== 'all' || activeBrands.length > 0 || activeAvailability.length > 0 || searchQuery) && (
                    <span className="text-xs text-gray-400 font-medium mr-1 hidden lg:inline">Your Filter:</span>
                  )}
                  {activeAvailability.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full capitalize">
                      {AVAILABILITY.find(a => a.id === activeAvailability[0])?.label || 'Availability'} {activeAvailability.length > 1 && `+${activeAvailability.length - 1}`} <button onClick={() => setActiveAvailability([])} className="hover:text-black">×</button>
                    </span>
                  )}
                  {activeBrands.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full capitalize">
                      Brand {activeBrands.length > 1 && `+${activeBrands.length - 1}`} <button onClick={() => setActiveBrands([])} className="hover:text-black">×</button>
                    </span>
                  )}
                  {(activeCategory !== 'all' || activeBrands.length > 0 || activeAvailability.length > 0 || searchQuery) && (
                    <button onClick={clearAllFilters} className="text-[10px] md:text-xs font-bold text-gray-900 underline underline-offset-2 hover:text-primary transition-colors whitespace-nowrap">
                      Clear All
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 cursor-pointer group whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-primary transition-colors">Default Sorting</span>
                    <KeyboardArrowDownIcon fontSize="small" className="text-gray-400 group-hover:text-primary" />
                  </div>
                  
                  <div className="items-center gap-1 hidden lg:flex border-l border-gray-200 pl-4 ml-2">
                    <button onClick={() => setViewLayout('grid-3')} className={viewLayout === 'grid-3' ? 'text-primary' : 'text-gray-300 hover:text-gray-500 transition-colors'}><ViewComfyIcon fontSize="small" /></button>
                    <button onClick={() => setViewLayout('grid-2')} className={viewLayout === 'grid-2' ? 'text-primary' : 'text-gray-300 hover:text-gray-500 transition-colors'}><ViewModuleIcon fontSize="small" /></button>
                    <button onClick={() => setViewLayout('list')} className={viewLayout === 'list' ? 'text-primary' : 'text-gray-300 hover:text-gray-500 transition-colors'}><ViewListIcon fontSize="small" /></button>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            {loading && (
              <div className="flex flex-col justify-center items-center py-20 gap-4">
                <CircularProgress size={50} thickness={4} sx={{ color: '#013e8a' }} />
                <p className="text-gray-500 font-semibold animate-pulse">Fetching live catalogue...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-20 bg-red-50 rounded-2xl border border-red-100">
                <p className="text-red-600 font-semibold text-lg">Failed to load product catalogue</p>
                <p className="text-red-500/70 text-sm mt-2">{error}</p>
              </div>
            )}

            {!loading && !error && (
              <div className="pl-0 lg:pl-6 w-full max-w-full">
                <div className={`
                  ${viewLayout === 'grid-3' ? 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-5 gap-y-6 md:gap-4 md:gap-y-6 items-stretch pb-28 md:pb-0' : ''}
                  ${viewLayout === 'grid-2' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6 md:gap-8 items-stretch pb-28 md:pb-0' : ''}
                  ${viewLayout === 'list' ? 'flex flex-col gap-6 pb-28 md:pb-0' : ''}
                  min-h-[50vh]
                `}>
                  {paginatedProducts.map((prod: Product) => (
                    <ProductCard key={prod.id} product={prod} className={`w-full ${viewLayout !== 'list' ? 'min-h-[340px] sm:min-h-[360px]' : ''}`} layout={viewLayout === 'list' ? 'list' : 'grid'} />
                  ))}
                </div>

                {filteredProducts.length === 0 && (
                  <div className="text-center py-20 mt-10">
                    <p className="text-gray-500 font-semibold">No products currently match your filters.</p>
                    <button onClick={clearAllFilters} className="mt-4 btn-outline !py-2 !px-4 !text-sm">Clear Filters</button>
                  </div>
                )}
                
                {/* Pagination: fixed-height area + compact page numbers to prevent layout shift / shake */}
                <div className="mt-16 border-t border-gray-100 pt-8 min-h-[72px] flex items-center justify-center">
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-1 sm:gap-2 text-sm font-bold text-gray-500 flex-wrap">
                      <button 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-100 text-gray-700 shrink-0"
                      >
                        <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                      </button>

                      {getPaginationPages().map((page, idx) =>
                        page === 'ellipsis' ? (
                          <span key={`ellipsis-${idx}`} className="w-10 h-10 flex items-center justify-center text-gray-400">…</span>
                        ) : (
                          <button 
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors shrink-0 ${
                              currentPage === page ? 'bg-primary text-white' : 'hover:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      )}

                      <button 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-100 text-gray-700 shrink-0"
                      >
                        <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopProducts;
